/**
 * Reopen an incomplete Import & Run from the ledger.
 *
 * The wizard itself is in-memory; this is the other half — fetch the run,
 * reload the file, and rebuild the console payload so the user lands on the
 * next unfinished step with their mappings intact.
 */
import type { LedgerAttempt, LedgerRun } from "./api";
import { coerceDataset, type DatasetKind } from "./dataset";
import {
  fetchDataHubLedgerRun,
  fetchDataHubStoredFile,
} from "../../Store/Reducers/DataHubConsoleSlice";
import type { AppDispatch } from "../../Store/Store";
import type { ColumnMapping } from "../lib/header-aliases";
import { parseSheet, type SheetTable } from "../lib/sheet-to-csv";

const PHASE1 = [
  "file",
  "columns",
  "accounts",
  "categories",
  "metrics",
  "import",
  "qc",
  "heal",
  "promote",
] as const;

const LEDGER_TO_STAGE: Record<string, string> = {
  file: "columns",
  columns: "columns",
  accounts: "accounts",
  categories: "categories",
  metrics: "metrics",
  import: "import",
  qc: "qc",
  heal: "heal",
  promote: "promote",
};

export type ResumeSnapshot = {
  stage: string;
  completed: string[];
  columns?: ColumnMapping[];
  accountDecisions?: Record<string, string>;
  metricDecisions?: Record<string, string>;
  categoryDecisions?: Record<string, string>;
  pipelineRunId?: string;
  batchId?: string | number;
};

export type HydratedRun = {
  file: File;
  table: SheetTable;
  dataset: DatasetKind;
  ledgerRunId: string;
  needsFile?: boolean;
  resume: ResumeSnapshot;
};

const EMPTY_TABLE: SheetTable = {
  headers: [],
  rows: [],
  sheet_name: "",
  all_sheet_names: [],
};

function asMapping(value: unknown): ColumnMapping[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const rows = value.filter(
    (row): row is ColumnMapping =>
      row &&
      typeof row === "object" &&
      typeof (row as ColumnMapping).header === "string" &&
      typeof (row as ColumnMapping).target === "string"
  );
  return rows.length ? rows : undefined;
}

function asDecisions(value: unknown): Record<string, string> | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value))
    return undefined;
  const out: Record<string, string> = {};
  Object.entries(value as Record<string, unknown>).forEach(([k, v]) => {
    if (typeof v === "string") out[k] = v;
  });
  return out;
}

function latestSucceeded(
  attempts: LedgerAttempt[] | undefined
): LedgerAttempt | null {
  const list = attempts ?? [];
  for (let i = list.length - 1; i >= 0; i--) {
    if (list[i].status === "succeeded") return list[i];
  }
  return null;
}

export function pipelineRunIdFromLedger(run: LedgerRun): string | null {
  const step = run.steps?.find(s => s.step === "import");
  const evidence = latestSucceeded(step?.attempts)?.artifact_evidence;
  const id = evidence?.pipeline_run_id;
  return id ? String(id) : null;
}

export function batchIdFromLedger(run: LedgerRun): string | number | undefined {
  const step = run.steps?.find(s => s.step === "import");
  const evidence = latestSucceeded(step?.attempts)?.artifact_evidence;
  const id = evidence?.batch_id;
  return id == null ? undefined : id;
}

function stageFromLedger(run: LedgerRun): string {
  if (run.progress?.phase_1_complete || !run.current_step) return "phase2";
  return LEDGER_TO_STAGE[run.current_step] ?? "columns";
}

function completedFromLedger(run: LedgerRun): string[] {
  return (run.steps ?? [])
    .filter(
      s =>
        s.status === "succeeded" &&
        (PHASE1 as readonly string[]).includes(s.step)
    )
    .map(s => s.step);
}

/**
 * Rebuild the console payload from a ledger run.
 *
 * Resume after Import can proceed from staging + saved decisions even when the
 * object store is empty. Resume before Import needs the file — `needsFile` is
 * set so the console can ask the user to re-select it.
 */
export async function hydrateActiveRun(
  dispatch: AppDispatch,
  runId: string
): Promise<HydratedRun> {
  const ledger = await dispatch(fetchDataHubLedgerRun(runId)).unwrap();
  const filename = ledger.source_filename || "import.csv";
  const dataset = coerceDataset(ledger.dataset_kind);
  const evidence = latestSucceeded(
    ledger.steps?.find(s => s.step === "import")?.attempts
  )?.artifact_evidence;
  const importDone = Boolean(evidence?.pipeline_run_id);

  let file = new File([], filename);
  let table = EMPTY_TABLE;
  let needsFile = true;

  if (ledger.storage_path) {
    try {
      file = await dispatch(
        fetchDataHubStoredFile({
          storage_path: ledger.storage_path,
          filename,
          size_bytes: 0,
          uploaded_at: "",
          file_hash: ledger.file_hash,
        })
      ).unwrap();
      table = await parseSheet(file, {
        sheetName: ledger.sheet_name || undefined,
      });
      needsFile = false;
    } catch {
      needsFile = !importDone;
    }
  } else {
    needsFile = !importDone;
  }

  return {
    file,
    table,
    dataset,
    ledgerRunId: ledger.id,
    needsFile: needsFile || undefined,
    resume: {
      stage: stageFromLedger(ledger),
      completed: completedFromLedger(ledger),
      columns: asMapping(ledger.decisions?.column_mapping),
      accountDecisions: asDecisions(ledger.decisions?.account_decisions),
      metricDecisions: asDecisions(ledger.decisions?.metric_decisions),
      categoryDecisions: asDecisions(ledger.decisions?.category_decisions),
      pipelineRunId: evidence?.pipeline_run_id
        ? String(evidence.pipeline_run_id)
        : undefined,
      batchId: evidence?.batch_id,
    },
  };
}
